// BubbleChatScreen.js - Enhanced Bubble Community Chat Component
// Production-ready with dual-mode casting call dialog support

import React, { useEffect, useMemo, useRef, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import { 
  Alert, Avatar, Box, Chip, Badge, Tooltip, CircularProgress, TextField, 
  IconButton, InputAdornment, Paper, Snackbar, Typography, Button,
  useMediaQuery, Card, CardContent, CardHeader, Divider, SpeedDial, 
  SpeedDialAction, Dialog, Fab
} from '@mui/material';
import { alpha, useTheme, styled } from '@mui/material/styles';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  Close as CloseIcon,
  Groups as GroupsIcon,
  Refresh as RefreshIcon,
  FiberManualRecord as FiberManualRecordIcon,
  Campaign as CampaignIcon,
  People as PeopleIcon,
  Public as PublicIcon,
  Lock as LockIcon,
  Info as InfoIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';

// Chat sub-components
import ChatBubble from './ChatBubble';
import ReadReceipt from './ReadReceipt';
import ReplyPreview from './ReplyPreview';
import AlertDialog from '../AlertDialog';
import SelectAssetDialog from '../tools/selectfile';
import ParticipantsPanel from './chatextra';
import ConversationDetailsPanel from './conversationdetailspanel';
import CreateCallDialog from '../../Pages/Dashboard/Campaigns/castingcalls/CreateCallDialog';

// Hooks & Utilities
import API from './../../API';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import useAuth from './../../Hooks/use-auth';
import { replaceConsoleError } from './../../Utils/toastNotifications';
import { useCreatorAuth } from './../../Hooks/creator-use-auth';
import useAlertDialog from '../useAlertDialog';
import useIsDesktop from '../../Hooks/useIsDesktop';
import { isMessageMine, getFileType, getTypingText } from '../../Utils/constants';
import AttachmentBubble from './AttachmentBubble';
import { useSocket } from '../../Context/SocketContext';
import useMentions from './useMentions';
import MentionSuggestions from './MentionSuggestions';

/* ───────────────────────── Styled Components ───────────────────────── */
const BubbleHeader = styled(Paper)(({ theme, themeColors }) => {
  const primaryColor = themeColors?.primary || theme.palette.primary.main;
  const secondaryColor = themeColors?.secondary || theme.palette.secondary.main;
  
  return {
    background: `linear-gradient(135deg, ${alpha(primaryColor, 0.1)} 0%, ${alpha(secondaryColor, 0.1)} 100%)`,
    borderRadius: '16px',
    border: `1px solid ${alpha(primaryColor, 0.2)}`,
    marginBottom: theme.spacing(2),
    overflow: 'hidden',
  };
});

const MemberChip = styled(Chip)(({ theme }) => ({
  borderRadius: 8,
  fontWeight: 600,
  fontSize: '0.75rem',
}));

const StatusIndicator = styled(Box)(({ theme, isOnline }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: isOnline ? theme.palette.success.main : theme.palette.warning.main,
  marginRight: theme.spacing(0.5),
}));

/* ───────────────────────── Constants ───────────────────────── */
const MAX_MESSAGES_WITHOUT_RESPONSE = 10;
const SCROLL_TIMEOUT = 100;
const SNACKBAR_DURATION = 6000;
const MAX_MESSAGE_ROWS = 3;
const POLLING_INTERVAL = 10000; // Fallback polling when socket disconnected
const NAV_BAR_HEIGHT = 86;
const SHOW_POLLING_INDICATOR_EVERY = 6;

const generateTempId = () => `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

/* ───────────────────────── Main Component ───────────────────────── */
const BubbleChatScreen = forwardRef(function BubbleChatScreen(
  {
    children,
    conversationId,
    bubbleId,
    onNavigateBack,
    showWarning = false,
    specificReaderId = null,
    chatSpacing = 1,
    margin = 2,
    sx = {},
    onMembersUpdate,
  },
  ref
) {
  const theme = useTheme();
  const { getCurrrentUser } = useAuth();
  const userInfo = getCurrrentUser();
  const { creatorToken } = useCreatorAuth();
  const queryClient = useQueryClient();
  const isDesktop = useIsDesktop();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { dialogState, openDialog, closeDialog } = useAlertDialog();
  const listRef = useRef(null);
  const lastMessageId = useRef(null);
  const pollingTimeoutRef = useRef(null);
  const isPollingRef = useRef(false);
  const pollCountRef = useRef(0);

  // WebSocket integration
  const {
    isConnected: socketConnected,
    joinConversation,
    leaveConversation,
    sendMessage: socketSendMessage,
    sendTypingStatus,
    markAsRead: socketMarkAsRead,
    addReaction: socketAddReaction,
    removeReaction: socketRemoveReaction,
    typingUsers,
    getTypingUsers,
    onlineUsers,
    getOnlineUsers,
    isUserOnline,
    roomPresence,
    getRoomOnlineUsers
  } = useSocket();

  /* ───────────────────────── State Management ───────────────────────── */
  const [message, setMessage] = useState('');
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [chatError, setChatError] = useState(null);
  const [currentMessage, setCurrentMessage] = useState(null);
  const [showMessageCapAlert, setShowMessageCapAlert] = useState(false);
  const [isManualRefresh, setIsManualRefresh] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [showPollingIndicator, setShowPollingIndicator] = useState(false);
  
  // Bubble-specific states
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [castingCallDialogOpen, setCastingCallDialogOpen] = useState(false);
  const [reactionLoading, setReactionLoading] = useState(new Set());
  const [snack, setSnack] = useState({ msg: '', sev: 'info' });
  const [replyToMessage, setReplyToMessage] = useState(null);
  const [isQuoting, setIsQuoting] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [pdfSigningDialogOpen, setPdfSigningDialogOpen] = useState(false);
  const [fileForSigning, setFileForSigning] = useState(null);
  
  // Optimistic messages for REST fallback
  const [optimisticMessages, setOptimisticMessages] = useState([]);

  // Typing indicator state
  const typingTimeoutRef = useRef(null);
  const isTypingRef = useRef(false);

  // Get typing users for this conversation
  const currentTypingUsers = useMemo(() => {
    return getTypingUsers(conversationId) || [];
  }, [getTypingUsers, conversationId, typingUsers]);

  /* ───────────────────────── Derived Variables ───────────────────────── */
  const userId = specificReaderId || userInfo?.id || creatorToken?.creator_user?.username || null;
  const userCompany = userInfo?.company_name;
  const isTcc = userCompany === "TCC";
  const isCreator = !!specificReaderId || (!!creatorToken && !userInfo);

  /* ───────────────────────── Data Fetching ───────────────────────── */
  
  // Fetch bubble metadata
  const {
    data: bubbleMetadata,
    isLoading: bubbleLoading,
    refetch: bubbleRefetch,
    error: bubbleError
  } = useQuery(
    ["bubble-metadata", bubbleId],
    () => API.bubbles.get(bubbleId),
    {
      enabled: !!bubbleId,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 30,
    }
  );

  // Fetch conversation metadata
  const {
    data: metadata,
    isLoading: metadataLoading,
    refetch: metadataRefetch,
    error: metadataError
  } = useQuery(
    ["bubble_messages", "metadata", conversationId],
    () => {
      return API.conversations.getMetadata(
        conversationId,
        specificReaderId !== null ? { reader_id: specificReaderId } : undefined
      );
    },
    {
      enabled: !!conversationId,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 30,
    }
  );

  // Fetch messages with optimized polling
  const {
    data: conversation,
    isLoading: messagesLoading,
    isFetching: messagesRefetching,
    refetch: messagesRefetch,
    error: messagesError,
    dataUpdatedAt: lastFetchTime
  } = useQuery(
    ["bubble_messages", conversationId],
    () => {
      return API.conversations.getMessages(
        conversationId,
        specificReaderId !== null ? { reader_id: specificReaderId } : undefined
      );
    },
    {
      enabled: !!metadata,
      // Disable refetch on window focus when socket is connected (socket handles real-time updates)
      refetchOnWindowFocus: !socketConnected,
      staleTime: 1000 * 30,
      cacheTime: 1000 * 60 * 10,
      onError: (err) => {
        replaceConsoleError('Bubble messages fetch error', err);
        setChatError('Failed to load messages');
      },
      onSuccess: (data) => {
        setChatError(null);
        setLastUpdateTime(new Date());
        setOptimisticMessages(prev =>
          prev.filter(opt =>
            !data.messages.some(real => real.id === opt.id || real.message === opt.message)
          )
        );
      }
    }
  );

  // @mention support - use same merging logic as chatextra.js
  const mentionParticipants = useMemo(() => {
    // Try all possible sources
    const metadataParticipants = metadata?.participants || [];
    const conversationParticipants = conversation?.participants || [];
    const globalParticipants = metadata?.conversation?.global_participants || [];
    
    const participantMap = new Map();
    
    // Add metadata participants first
    metadataParticipants.forEach(p => {
      const key = String(p.global_user_id || p.id);
      participantMap.set(key, {
        ...p,
        isGlobalParticipant: false,
        isRegularParticipant: true,
        global_user_id: p.global_user_id || p.id,
      });
    });
    
    // Add conversation participants
    conversationParticipants.forEach(p => {
      const key = String(p.global_user_id || p.id);
      if (!participantMap.has(key)) {
        participantMap.set(key, {
          ...p,
          isGlobalParticipant: false,
          isRegularParticipant: true,
          global_user_id: p.global_user_id || p.id,
        });
      }
    });
    
    // Merge global participants
    globalParticipants.forEach(gp => {
      const key = String(gp.id);
      const existing = participantMap.get(key);
      
      if (existing) {
        participantMap.set(key, {
          ...existing,
          ...gp,
          isGlobalParticipant: true,
          global_user_id: gp.id,
        });
      } else {
        participantMap.set(key, {
          ...gp,
          id: gp.id,
          username: gp.username,
          pfp: gp.avatar || gp.pfp,
          is_creator: gp.user_type === 'creator',
          isGlobalParticipant: true,
          isRegularParticipant: false,
          global_user_id: gp.id,
        });
      }
    });
    
    return Array.from(participantMap.values());
  }, [metadata?.participants, conversation?.participants, metadata?.conversation?.global_participants]);
  const mentions = useMentions(mentionParticipants, userId);

  /* ───────────────────────── WebSocket Room Management ───────────────────────── */
  useEffect(() => {
    if (!conversationId) return;

    // Join the conversation room when socket is connected
    if (socketConnected) {
      joinConversation(conversationId);
      socketMarkAsRead(conversationId);
    }

    return () => {
      if (socketConnected) {
        leaveConversation(conversationId);
      }
    };
  }, [conversationId, socketConnected, joinConversation, leaveConversation, socketMarkAsRead]);

  /* ───────────────────────── Fallback Polling System ───────────────────────── */
  const startPolling = useCallback(() => {
    // Don't poll if socket is connected
    if (isPollingRef.current || !conversationId || socketConnected) return;
    
    isPollingRef.current = true;
    pollCountRef.current = 0;
    
    const poll = async () => {
      // Stop polling if socket became connected
      if (socketConnected) {
        stopPolling();
        return;
      }

      try {
        if (document.hidden || !conversationId) {
          pollingTimeoutRef.current = setTimeout(poll, POLLING_INTERVAL * 2);
          return;
        }

        pollCountRef.current++;
        const shouldShowIndicator = pollCountRef.current % SHOW_POLLING_INDICATOR_EVERY === 0;
        
        if (shouldShowIndicator) {
          setShowPollingIndicator(true);
        }

        await messagesRefetch();
        
        if (shouldShowIndicator) {
          setTimeout(() => setShowPollingIndicator(false), 1000);
        }

        pollingTimeoutRef.current = setTimeout(poll, POLLING_INTERVAL);
      } catch (error) {
        console.error('Polling error:', error);
        setShowPollingIndicator(false);
        pollingTimeoutRef.current = setTimeout(poll, POLLING_INTERVAL * 2);
      }
    };

    pollingTimeoutRef.current = setTimeout(poll, POLLING_INTERVAL);
  }, [conversationId, messagesRefetch, socketConnected]);

  const stopPolling = useCallback(() => {
    isPollingRef.current = false;
    setShowPollingIndicator(false);
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else if (conversationId && !socketConnected) {
        startPolling();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Only start polling if socket is NOT connected
    if (conversationId && !document.hidden && !socketConnected) {
      startPolling();
    } else if (socketConnected) {
      stopPolling();
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stopPolling();
    };
  }, [conversationId, startPolling, stopPolling, socketConnected]);
const bubble = bubbleMetadata?.bubble || metadata?.bubble;

  /* ───────────────────────── Message Processing ───────────────────────── */
  const allMessages = useMemo(() => {
    const serverMessages = conversation?.messages || [];
    // Combine server messages with optimistic messages, filtering out any optimistic
    // messages that have been confirmed (matching by client_temp_id)
    const serverTempIds = new Set(
      serverMessages
        .filter(m => m.client_temp_id)
        .map(m => m.client_temp_id)
    );
    const pendingOptimistic = optimisticMessages.filter(
      m => !serverTempIds.has(m.client_temp_id)
    );
    return [...serverMessages, ...pendingOptimistic];
  }, [conversation?.messages, optimisticMessages]);

  /* ───────────────────────── Mutations ───────────────────────── */
  const { mutate: deleteMessage, isLoading: deleteMessageLoading } = useMutation(
    (msgId) => API.conversations.deleteMessage(msgId),
    {
      onSuccess: () => {
        messagesRefetch();
      },
      onError: (error) => {
        replaceConsoleError('Delete message error', error);
        openDialog(
          "Error",
          `Error while deleting message: ${error?.response?.data?.error || error.message}`
        );
      }
    }
  );

  const { mutateAsync: addReactionAsync } = useMutation(
  ({ messageId, reactionType }) => API.conversations.addReaction(messageId, reactionType),
  {
    onSuccess: () => {
      messagesRefetch();
    },
    onError: (error) => {
      replaceConsoleError('Error adding reaction', error);
      setSnack({ msg: 'Failed to add reaction', sev: 'error' });
    }
  }
);

const { mutateAsync: removeReactionAsync } = useMutation(
  ({ messageId, reactionType }) => API.conversations.removeReaction(messageId, reactionType),
  {
    onSuccess: () => {
      messagesRefetch();
    },
    onError: (error) => {
      replaceConsoleError('Error removing reaction', error);
      setSnack({ msg: 'Failed to remove reaction', sev: 'error' });
    }
  }
);

// ✅ FIXED: Properly handle async mutations with socket fallback
const handleReactionAdd = useCallback(async (messageId, reactionType) => {
  if (reactionLoading.has(messageId)) return;
  
  setReactionLoading(prev => new Set(prev.add(messageId)));
  
  try {
    // Use socket when connected, REST API as fallback
    if (socketConnected && conversationId) {
      socketAddReaction(conversationId, messageId, reactionType);
      setSnack({ msg: 'Reaction added!', sev: 'success' });
    } else {
      await addReactionAsync({ messageId, reactionType });
      setSnack({ msg: 'Reaction added!', sev: 'success' });
    }
  } catch (error) {
    // Error already handled in mutation onError
    console.error('Reaction add error:', error);
    setSnack({ msg: 'Failed to add reaction', sev: 'error' });
  } finally {
    setReactionLoading(prev => {
      const newSet = new Set(prev);
      newSet.delete(messageId);
      return newSet;
    });
  }
}, [addReactionAsync, reactionLoading, socketConnected, conversationId, socketAddReaction]);

  const { mutate: sendMessage, isLoading: sendMessageLoading } = useMutation(
    ({ id, input }) => API.conversations.sendMessage(id, input),
    {
      onMutate: async ({ input }) => {
        const tempId = generateTempId();
        const optimisticMessage = {
          id: tempId,
          client_temp_id: tempId,
          message: input.message || 'Attachment',
          sender_id: isCreator ? null : userId,
          creator_id: isCreator ? userId : null,
          created_at: new Date().toISOString(),
          isOptimistic: true,
          is_pending: true,
          attachments: input.attachment ? {
            attachment_type: input.attachment.type,
            data: input.attachment.data,
            label: input.attachment.label
          } : null,
          read_receipts: {},
          reply_to_message_id: input.reply_to_message_id || null,
          is_quote: input.is_quote || false,
          reply_to: replyToMessage ? {
            id: replyToMessage.id,
            message: replyToMessage.message,
            sender_name: replyToMessage.sender?.first_name || replyToMessage.creator_id || 'User',
            created_at: replyToMessage.created_at
          } : null
        };

        setOptimisticMessages(prev => [...prev, optimisticMessage]);
        
        setMessage('');
        setSelectedAttachment(null);
        setReplyToMessage(null);
        setIsQuoting(false);

        setTimeout(() => {
          if (listRef.current) {
            listRef.current.scrollTo({
              top: listRef.current.scrollHeight,
              behavior: "smooth"
            });
          }
        }, 50);

        return { tempId };
      },
      onSuccess: (data, variables, context) => {
        if (context?.tempId) {
          setOptimisticMessages(prev =>
            prev.filter(msg => msg.client_temp_id !== context.tempId)
          );
        }
        messagesRefetch();
      },
      onError: (error, variables, context) => {
        replaceConsoleError("Error sending message", error);
        
        if (context?.tempId) {
          // Mark as failed instead of removing
          setOptimisticMessages(prev =>
            prev.map(msg => 
              msg.client_temp_id === context.tempId 
                ? { ...msg, is_pending: false, is_failed: true }
                : msg
            )
          );
        }

        setMessage(variables.input.message || '');
        if (variables.input.attachment) {
          setSelectedAttachment(variables.input.attachment);
        }
        if (variables.input.reply_to_message_id) {
          setReplyToMessage(replyToMessage);
          setIsQuoting(variables.input.is_quote || false);
        }

        handleSendMessageError(error);
      }
    }
  );

  const { mutate: addReaction } = useMutation(
    ({ messageId, reactionType }) => API.conversations.addReaction(messageId, reactionType),
    {
      onSuccess: () => {
        setSnack({ msg: 'Reaction added!', sev: 'success' });
        messagesRefetch();
      },
      onError: (error) => {
        replaceConsoleError('Error adding reaction', error);
        setSnack({ msg: 'Failed to add reaction', sev: 'error' });
      }
    }
  );

  const { mutate: removeReaction } = useMutation(
    (messageId) => API.conversations.removeReaction(messageId),
    {
      onSuccess: () => {
        setSnack({ msg: 'Reaction removed!', sev: 'success' });
        messagesRefetch();
      },
      onError: (error) => {
        replaceConsoleError('Error removing reaction', error);
        setSnack({ msg: 'Failed to remove reaction', sev: 'error' });
      }
    }
  );

  /* ───────────────────────── Event Handlers ───────────────────────── */
  const userMessagesCount = useMemo(() => {
    if (!allMessages || allMessages.length === 0) return 0;
    
    const userMessages = allMessages.filter((m) => isMessageMine(userId, m));
    const otherMessages = allMessages.filter((m) => !isMessageMine(userId, m));
    
    if (otherMessages.length > 0) {
      // Sort by time to ensure we get the actual last message from other party
      const sortedOtherMessages = [...otherMessages].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      const lastOtherMsgTime = new Date(sortedOtherMessages[0].created_at).getTime();
      
      // Count only user messages sent AFTER the last response from other party
      return userMessages.filter(
        (m) => new Date(m.created_at).getTime() > lastOtherMsgTime
      ).length;
    }
    
    return userMessages.length;
  }, [allMessages, userId]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (listRef.current && allMessages.length > 0) {
        const latestMessage = allMessages[allMessages.length - 1];
        if (latestMessage.id !== lastMessageId.current) {
          listRef.current.scrollTo({
            top: listRef.current.scrollHeight,
            behavior: "smooth"
          });
          lastMessageId.current = latestMessage.id;
        }
      }
    }, SCROLL_TIMEOUT);
    return () => clearTimeout(timeoutId);
  }, [allMessages]);

  const handleManualRefresh = useCallback(async () => {
    setIsManualRefresh(true);
    try {
      await messagesRefetch();
      await metadataRefetch();
      if (bubbleId) await bubbleRefetch();
    } finally {
      setIsManualRefresh(false);
    }
  }, [messagesRefetch, metadataRefetch, bubbleRefetch, bubbleId]);

  const handleEditFile = useCallback((file) => {
    setFileForSigning(file);
    setPdfSigningDialogOpen(true);
  }, []);

  const handleVideoLink = useCallback((videoId) => {
    setSnack({ msg: 'Video link viewing coming soon!', sev: 'info' });
  }, []);

  const isMessageAllowed = useCallback((text) => {
    const prohibitedKeywords = [
      "text me", "contact me", "contact", "@gmail.com",
      "whatsapp", "convenient contact"
    ];
    const phoneRegex = /(?:\d[\s-]?){10,}/;
    const urlRegex = /https?:\/\/[^\s]+/g;
    const textNoUrls = text.replace(urlRegex, '');
    const lowerText = textNoUrls.toLowerCase();

    if (prohibitedKeywords.some(k => lowerText.includes(k))) {
      return false;
    }
    return !phoneRegex.test(textNoUrls);
  }, []);

  // Location: BubbleChatScreen.js
// Replace the handleSendMessage callback (around line 680)

const handleSendMessage = useCallback(async () => {
  // ✅ NEW: Check if user has permission to post
  if (bubble && !bubble.can_create_posts) {
    openDialog(
      "Posting Disabled",
      "Only admins can create posts in this bubble. Members have read-only access."
    );
    return;
  }

  if (userMessagesCount >= MAX_MESSAGES_WITHOUT_RESPONSE && !isTcc) {
    setShowMessageCapAlert(true);
    return;
  }

  if (!isMessageAllowed(message)) {
    openDialog(
      "Error",
      "Your message contains prohibited content. Please refrain from sharing personal contact information."
    );
    return;
  }

  if ((!message || message.trim() === '') && !selectedAttachment) {
    openDialog("Error", "Can't send an empty message");
    return;
  }

  if (!conversationId) {
    openDialog("Error", "No valid conversation. Please contact support if this persists.");
    return;
  }

  // Stop typing indicator
  if (isTypingRef.current) {
    isTypingRef.current = false;
    sendTypingStatus(conversationId, false);
  }

  const inputData = {
    message,
    ...(specificReaderId && { reader_id: specificReaderId }),
    ...(selectedAttachment && { attachment: selectedAttachment }),
    ...(replyToMessage && { 
      reply_to_message_id: replyToMessage.id,
      is_quote: isQuoting 
    })
  };

  // Use socket when connected, REST API as fallback
  if (socketConnected) {
    const tempId = generateTempId();
    const optimisticMessage = {
      id: tempId, // Temporary ID, will be replaced by server
      client_temp_id: tempId,
      message: message || 'Attachment',
      sender_id: isCreator ? null : userId,
      creator_id: isCreator ? userId : null,
      created_at: new Date().toISOString(),
      isOptimistic: true,
      is_pending: true, // Show single tick (pending state)
      attachments: selectedAttachment ? {
        attachment_type: selectedAttachment.type,
        data: selectedAttachment.data,
        label: selectedAttachment.label
      } : null,
      read_receipts: {},
      reply_to_message_id: replyToMessage?.id || null,
      is_quote: isQuoting,
      reply_to: replyToMessage ? {
        id: replyToMessage.id,
        message: replyToMessage.message,
        sender_name: replyToMessage.sender?.first_name || replyToMessage.creator_id || 'User',
        created_at: replyToMessage.created_at
      } : null
    };

    // ✅ Unified state: update query cache directly
    queryClient.setQueryData(["bubble_messages", conversationId], (old) => ({
      ...old,
      messages: [...(old?.messages || []), optimisticMessage]
    }));

    setMessage('');
    setSelectedAttachment(null);
    setReplyToMessage(null);
    setIsQuoting(false);

    setTimeout(() => {
      if (listRef.current) {
        listRef.current.scrollTo({ top: listRef.current.scrollHeight, behavior: "auto" });
      }
    }, 0);

    // Send via socket with correct payload structure
    socketSendMessage({
      conversation_id: conversationId,
      message: message,
      attachment_id: selectedAttachment?.data?.id || null,
      reply_to_message_id: replyToMessage?.id || null,
      is_quote: isQuoting,
      client_temp_id: tempId
    });
  } else {
    sendMessage({ id: conversationId, input: inputData });
  }
}, [
  bubble,
  message,
  selectedAttachment,
  conversationId,
  specificReaderId,
  userMessagesCount,
  isTcc,
  isMessageAllowed,
  openDialog,
  sendMessage,
  replyToMessage,
  isQuoting,
  socketConnected,
  socketSendMessage,
  sendTypingStatus,
  isCreator,
  userId,
  queryClient
]);

  const handleSendMessageError = useCallback((error) => {
    if (error?.response?.data?.error === "forbidden") {
      openDialog(
        "Error",
        "This message was flagged as containing prohibited content. Please refrain from sharing personal contact information.",
        closeDialog,
        closeDialog,
        "Ok",
        null
      );
      return;
    }
    openDialog(
      "Error",
      `Error while sending message: ${error?.response?.data?.error || error.message}`
    );
  }, [openDialog, closeDialog]);

 

  const handleReactionRemove = useCallback(async (messageId, reactionType) => {
    if (reactionLoading.has(messageId)) return;
    
    setReactionLoading(prev => new Set(prev.add(messageId)));
    
    try {
      if (socketConnected && conversationId) {
        socketRemoveReaction(conversationId, messageId, reactionType);
        setSnack({ msg: 'Reaction removed!', sev: 'success' });
      } else {
        await removeReactionAsync({ messageId, reactionType });
        setSnack({ msg: 'Reaction removed!', sev: 'success' });
      }
    } catch (error) {
      console.error('Reaction remove error:', error);
      setSnack({ msg: 'Failed to remove reaction', sev: 'error' });
    } finally {
      setReactionLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(messageId);
        return newSet;
      });
    }
  }, [removeReactionAsync, reactionLoading, socketConnected, conversationId, socketRemoveReaction]);

  const handleReply = useCallback((message) => {
    setReplyToMessage(message);
    setIsQuoting(false);
    setTimeout(() => {
      const input = document.querySelector('textarea[name="bubble-chat-message"]');
      if (input) input.focus();
    }, 100);
  }, []);

  const handleQuote = useCallback((message) => {
    setReplyToMessage(message);
    setIsQuoting(true);
    setTimeout(() => {
      const input = document.querySelector('textarea[name="bubble-chat-message"]');
      if (input) input.focus();
    }, 100);
  }, []);

  const handleCancelReply = useCallback(() => {
    setReplyToMessage(null);
    setIsQuoting(false);
  }, []);

  // Handle typing indicator
  const handleTyping = useCallback((newMessage) => {
    setMessage(newMessage);
    
    if (!socketConnected || !conversationId) return;
    
    // Send typing status via socket
    if (newMessage.trim() && !isTypingRef.current) {
      isTypingRef.current = true;
      sendTypingStatus(conversationId, true);
    }
    
    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Set timeout to stop typing indicator after 2 seconds of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      if (isTypingRef.current) {
        isTypingRef.current = false;
        sendTypingStatus(conversationId, false);
      }
    }, 2000);
  }, [socketConnected, conversationId, sendTypingStatus]);

  // Cleanup typing timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (isTypingRef.current && socketConnected && conversationId) {
        sendTypingStatus(conversationId, false);
      }
    };
  }, [socketConnected, conversationId, sendTypingStatus]);

  const scrollToMessage = useCallback((messageId) => {
    if (!messageId) return;
    
    const messageElement = document.getElementById(`msg-${messageId}`);
    if (messageElement) {
      messageElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center'
      });
      
      messageElement.style.transition = 'background-color 0.3s ease';
      messageElement.style.backgroundColor = alpha(theme.palette.primary.main, 0.1);
      
      setTimeout(() => {
        messageElement.style.backgroundColor = 'transparent';
      }, 2000);
    } else {
      if (listRef.current) {
        listRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        setTimeout(() => {
          const retryElement = document.getElementById(`msg-${messageId}`);
          if (retryElement) {
            retryElement.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center' 
            });
          }
        }, 500);
      }
    }
  }, [theme.palette.primary.main]);

  const handleReactionChange = useCallback((data) => {
    setSnack({ 
      msg: `${data.reaction ? 'Reaction added' : 'Reaction removed'}!`, 
      sev: "success" 
    });
  }, []);

  const handleDeleteMessage = useCallback((messageId) => {
    if (isTcc) {
      openDialog(
        "Warning",
        "Are you sure you want to delete this message?",
        () => {
          closeDialog();
          deleteMessage(messageId);
        },
        closeDialog,
        "Yes",
        "No"
      );
    }
  }, [isTcc, openDialog, closeDialog, deleteMessage]);

  // File attachment handlers
  const handleFileSelect = useCallback((file) => {
    const isImage = getFileType(file.filename) === 'image';
    setSelectedAttachment({
      type: 'file',
      label: file.filename,
      data: { id: file.id },
      preview: isImage ? file.file_url : undefined
    });
    setSelectedAsset(file);
    setUploadDialogOpen(false);
    setMessage('');
  }, []);

  const handleAssetClick = useCallback((asset) => {
    handleFileSelect(asset);
  }, [handleFileSelect]);

  const handleAssetDialogClose = useCallback(() => {
    setUploadDialogOpen(false);
  }, []);

  const handleToggleParticipants = useCallback(() => {
    setParticipantsOpen(prev => !prev);
  }, []);

  const handleToggleDetails = useCallback(() => {
    setDetailsOpen(prev => !prev);
  }, []);

  const handleCastingCallSuccess = useCallback((newCastingCall) => {
    setCastingCallDialogOpen(false);
    
    if (conversationId) {
      queryClient.invalidateQueries(["bubble_messages", conversationId]);
    }
    if (bubbleId) {
      queryClient.invalidateQueries(["bubble-feed", bubbleId]);
    }
    
    if (newCastingCall && newCastingCall.casting_call?.id) {
      setSnack({ 
        msg: 'Casting call created and shared!', 
        sev: 'success' 
      });
    } else {
      setSnack({ 
        msg: 'Casting call created successfully!', 
        sev: 'success' 
      });
    }
    
    messagesRefetch();
  }, [conversationId, bubbleId, messagesRefetch, queryClient]);

  /* ───────────────────────── Imperative Handle ───────────────────────── */
  useImperativeHandle(ref, () => ({
    scrollToTop() {
      listRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    },
    refreshMessages() {
      messagesRefetch();
    },
    scrollToMessage,
  }));

  /* ───────────────────────── Update Members ───────────────────────── */
  useEffect(() => {
    if (onMembersUpdate && metadata?.participants) {
      onMembersUpdate(metadata.participants);
    }
  }, [metadata?.participants, onMembersUpdate]);

  /* ───────────────────────── Online Status (per-room presence) ───────────────────────── */
  const roomOnline = useMemo(() => getRoomOnlineUsers(conversationId), [getRoomOnlineUsers, conversationId, roomPresence]);
  const onlineUsersCount = roomOnline.online_count || 0;
  const onlineUsersList = roomOnline.online_users || [];

  /* ───────────────────────── Render ───────────────────────── */
  const getDialogZIndex = () => {
    if (isMobile) {
      return 1500;
    }
    return 1400;
  };

  return (
    <>
      <Box
        id='bubble-chat-holder'
        sx={{
          ...sx,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: '100%',
          pb: (theme) =>
            window.location.pathname.includes("/con") ? 0 : `${NAV_BAR_HEIGHT}px`,
        }}
      >
        {/* Bubble Header - Only show on mobile */}
        {/* Bubble Header - Show on all devices */}
<BubbleHeader themeColors={bubble?.theme_colors} elevation={2}>
  <CardHeader
    avatar={
      isMobile ? (
        <IconButton 
          onClick={onNavigateBack}
          sx={{ mr: 1 }}
          edge="start"
        >
          <ArrowBackIcon />
        </IconButton>
      ) : null
    }
    title={
      <Box display="flex" alignItems="center" gap={1}>
        {bubble?.avatar_image && (
          <Avatar 
            src={bubble.avatar_image} 
            sx={{ 
              width: isMobile ? 32 : 40, 
              height: isMobile ? 32 : 40,
              border: (theme) => `2px solid ${alpha(theme.palette.primary.main, 0.1)}`
            }} 
          />
        )}
        <Typography variant={isMobile ? "h6" : "h5"} fontWeight={700}>
          {bubble?.name || 'Bubble Chat'}
        </Typography>
        {bubble?.is_public ? (
          <Tooltip title="Public Bubble">
            <PublicIcon fontSize="small" sx={{ color: 'success.main' }} />
          </Tooltip>
        ) : (
          <Tooltip title="Private Bubble">
            <LockIcon fontSize="small" color="action" />
          </Tooltip>
        )}
      </Box>
    }
    subheader={
      <Box display="flex" alignItems="center" gap={1.5} mt={0.5}>
        <MemberChip 
          label={`${bubble?.member_count || metadata?.participants?.length || 0} members`}
          size="small"
          icon={<PeopleIcon />}
          color="success"
          variant="outlined"
        />
        <Tooltip title={onlineUsersCount > 0 ? onlineUsersList.map(u => u.display_name).join(', ') : (socketConnected ? 'No one online' : 'Offline')}>
          <Box display="flex" alignItems="center">
            <StatusIndicator isOnline={onlineUsersCount > 0} />
            <Typography variant="caption" color="text.secondary">
              {onlineUsersCount > 0 
                ? `${onlineUsersCount} online` 
                : (socketConnected ? 'No one online' : 'Offline')}
            </Typography>
          </Box>
        </Tooltip>
      </Box>
    }
    action={
      <Box display="flex" gap={1}>
        {!isMobile && onNavigateBack && (
          <Tooltip title="Back to conversations">
            <IconButton
              size="small"
              onClick={onNavigateBack}
              sx={{
                bgcolor: alpha(theme.palette.action.active, 0.08),
                '&:hover': { bgcolor: alpha(theme.palette.action.active, 0.15) }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
        )}
        
        <Tooltip title="Bubble Info">
          <IconButton
            size="small"
            onClick={handleToggleDetails}
            sx={{
              bgcolor: alpha(theme.palette.info.main, 0.1),
              '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.2) }
            }}
          >
            <InfoIcon />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Refresh messages">
          <IconButton
            size="small"
            onClick={handleManualRefresh}
            disabled={isManualRefresh}
          >
            <RefreshIcon
              sx={{
                animation: isManualRefresh ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                }
              }}
            />
          </IconButton>
        </Tooltip>
      </Box>
    }
    sx={{ pb: 1 }}
  />
  
  {bubble?.description && (
    <>
      <Divider sx={{ mx: 2 }} />
      <Box px={2} pb={2} pt={1}>
        <Typography variant="body2" color="text.secondary">
          {bubble.description}
        </Typography>
      </Box>
    </>
  )}
</BubbleHeader>

{/* ✅ NEW: Read-Only Mode Alert */}
{bubble && !bubble.can_create_posts && (
  <Alert 
    severity="info" 
    icon={<LockIcon />}
    sx={{ mx: margin, mb: 1 }}
  >
    <Typography variant="body2">
      This bubble is in <strong>read-only mode</strong>. Only admins can create posts.
    </Typography>
  </Alert>
)}

        {/* Warning Banner */}
        {showWarning && (
          <Paper
            elevation={2}
            sx={{
              paddingBlock: 1,
              paddingInline: 2,
              backgroundColor: "#fff3cd",
              mx: margin,
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Typography variant="body2" sx={{ color: "#856404", flex: 1, textAlign: "center" }}>
              ⚠️ Please be respectful and follow community guidelines
            </Typography>
          </Paper>
        )}

        {/* Messages Container */}
        <Box
          display="flex"
          flexDirection="column"
          sx={{
            minHeight: 0,
            paddingInline: margin,
            paddingBlockStart: 1,
            minHeight: "300px",
            minWidth: "0px",
            width: "100%",
            overflowY: "auto",
            overflowX: "clip",
            flex: 1,
            gap: chatSpacing,
            '&::-webkit-scrollbar': {
              width: 6,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(theme.palette.text.primary, 0.2),
              borderRadius: 3,
            },
          }}
          ref={listRef}
        >
          {/* Loading States */}
          {messagesLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {messagesError && (
            <Paper
              sx={{
                p: 2,
                bgcolor: alpha(theme.palette.error.main, 0.1),
                color: 'error.main',
                textAlign: 'center'
              }}
            >
              <Typography>
                Error loading messages: {messagesError?.response?.data?.error || messagesError.message}
              </Typography>
              <Button
                onClick={handleManualRefresh}
                sx={{ mt: 1 }}
                variant="outlined"
                size="small"
              >
                Retry
              </Button>
            </Paper>
          )}

          {chatError && (
            <Typography color="error" sx={{ textAlign: 'center', p: 2 }}>
              {chatError}
            </Typography>
          )}

          {allMessages.length === 0 && !messagesError && !messagesLoading && (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
              color: "text.secondary",
              textAlign: "center"
            }}>
              <GroupsIcon sx={{ fontSize: 64, mb: 2, color: 'success.main', opacity: 0.5 }} />
              <Typography variant="h6" gutterBottom>
                No messages yet
              </Typography>
              <Typography variant="body2">
                Be the first to start the conversation in this bubble!
              </Typography>
            </Box>
          )}

          {/* Enhanced Message Rendering */}
          {allMessages.map((msg) => {
            const isUserMessage = isMessageMine(userId, msg);
            const justify = isUserMessage ? "flex-end" : "flex-start";
            const isSimpleMsg = !msg.attachments;

            return (
              <Box
                key={msg.id}
                id={`msg-${msg.id}`}
                sx={{
                  display: "flex",
                  justifyContent: justify,
                  maxWidth: "100%",
                  transition: "all 0.2s",
                  position: 'relative'
                }}
              >

                {/* Render AttachmentBubble for messages with attachments */}
                {msg.attachments && (
                  <AttachmentBubble
                    msg={msg}
                    userId={userId}
                    isTcc={isTcc}
                    isUser={!isCreator}
                    metadata={metadata}
                    handleDeleteMessage={handleDeleteMessage}
                    deleteMessageLoading={deleteMessageLoading}
                    onEditFile={handleEditFile}
                    handleVideoLink={handleVideoLink}
                  />
                )}

                {/* Render ChatBubble only for simple text messages */}
                {isSimpleMsg && (
                  <ChatBubble
                    msg={msg}
                    userId={userId}
                    isTcc={isTcc}
                    handleDeleteMessage={handleDeleteMessage}
                    deleteMessageLoading={deleteMessageLoading}
                    handleReadReceipts={(selectedMsg) => setCurrentMessage(selectedMsg)}
                    participants={conversation?.participants}
                    conversationId={conversationId}
                    onReactionChange={handleReactionChange}
                    onReactionAdd={handleReactionAdd}
                    onReactionRemove={handleReactionRemove}
                    onReply={handleReply}
                    onQuote={handleQuote}
                    onReplyClick={scrollToMessage}
                    sxMsgText={{ overflowWrap: 'anywhere' }}
                  />
                )}
              </Box>
            );
          })}

          {/* Status Indicators */}
          {isManualRefresh && (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              py: 1,
              position: 'sticky',
              bottom: 0,
              bgcolor: alpha(theme.palette.background.paper, 0.9),
              backdropFilter: 'blur(4px)'
            }}>
              <Chip
                size="small"
                label="Refreshing messages..."
                color="primary"
                variant="outlined"
                icon={<RefreshIcon sx={{ fontSize: 16 }} />}
              />
            </Box>
          )}

          {showPollingIndicator && !isManualRefresh && (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              py: 0.5,
              position: 'sticky',
              bottom: 0,
            }}>
              <Chip
                size="small"
                label="Syncing..."
                color="default"
                variant="outlined"
                sx={{
                  fontSize: '0.7rem',
                  height: 24,
                  opacity: 0.7,
                  bgcolor: alpha(theme.palette.background.paper, 0.8),
                  backdropFilter: 'blur(4px)'
                }}
              />
            </Box>
          )}
        </Box>

        {/* Message Input Area */}
        <Box sx={{ paddingBlock: 2, paddingInline: margin }}>
          {children}

          {/* Reply Preview */}
          {replyToMessage && (
            <ReplyPreview
              replyToMessage={replyToMessage}
              isQuote={isQuoting}
              onCancel={handleCancelReply}
            />
          )}

          <Box display="flex" alignItems="center" gap={1}>
            {/* Attach File SpeedDial */}
            <Box sx={{ width: "56px", height: "56px", position: "relative" }}>
              <SpeedDial
                ariaLabel="Attachment Menu"
                icon={<AttachFileIcon />}
                sx={{ position: "absolute", bottom: 0, right: 0 }}
                FabProps={{
                  disabled: sendMessageLoading || messagesLoading || !conversationId,
                  sx: { bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' } }
                }}
              >
                <SpeedDialAction
                  icon={<ImageIcon />}
                  tooltipTitle="Attach Image"
                  onClick={() => setUploadDialogOpen(true)}
                />
                <SpeedDialAction
                  icon={<FileIcon />}
                  tooltipTitle="Attach File"
                  onClick={() => setUploadDialogOpen(true)}
                />
              </SpeedDial>
            </Box>

            {/* Casting Call Button - Only for non-creators */}
            {!isCreator && (
              <Box sx={{ width: "56px", height: "56px", position: "relative" }}>
                <Tooltip title="Create Casting Call" arrow>
                  <span>
                    <Fab
                      sx={{ 
                        position: "absolute", 
                        bottom: 0, 
                        right: 0
                      }}
                      color="primary"
                      aria-label="create casting call"
                      onClick={() => setCastingCallDialogOpen(true)}
                      disabled={sendMessageLoading || messagesLoading || !conversationId}
                    >
                      <CampaignIcon />
                    </Fab>
                  </span>
                </Tooltip>
              </Box>
            )}

            {/* Typing Indicator */}
            {currentTypingUsers.length > 0 && (
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5, 
                  mb: 1,
                  py: 0.5,
                  color: 'text.secondary',
                  fontSize: '0.75rem'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    gap: '2px',
                    '& > span': {
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      backgroundColor: 'text.secondary',
                      animation: 'typing 1.4s infinite',
                      '&:nth-of-type(2)': { animationDelay: '0.2s' },
                      '&:nth-of-type(3)': { animationDelay: '0.4s' },
                    },
                    '@keyframes typing': {
                      '0%, 60%, 100%': { transform: 'translateY(0)' },
                      '30%': { transform: 'translateY(-4px)' },
                    },
                  }}
                >
                  <span /><span /><span />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {getTypingText(currentTypingUsers)}
                </Typography>
              </Box>
            )}

            {/* Text Field with @mention support */}
            <Box sx={{ flex: 1, position: 'relative' }}>
              <MentionSuggestions
                isVisible={mentions.isVisible}
                participants={mentions.filteredParticipants}
                selectedIndex={mentions.selectedIndex}
                onSelect={(p) => {
                  const newMsg = mentions.insertMention(p, message);
                  setMessage(newMsg);
                }}
                getDisplayName={mentions.getDisplayName}
              />
              <TextField
                  multiline
                  maxRows={MAX_MESSAGE_ROWS}
                  value={message}
                  autoComplete='off'
                  name='bubble-chat-message'
                  inputRef={mentions.inputRef}
                  disabled={
                    sendMessageLoading || 
                    messagesLoading || 
                    !conversationId || 
                    !!selectedAttachment ||
                    (bubble && !bubble.can_create_posts)
                  }
                  onChange={(e) => {
                    handleTyping(e.target.value);
                    mentions.onTextChange(e.target.value, e.target.selectionStart);
                  }}
                  label={bubble && !bubble.can_create_posts ? "Read-only (admins only)" : "Type a message"}
                  variant="outlined"
                  fullWidth
                  onBlur={() => setTimeout(() => mentions.closeSuggestions(), 150)}
                  onKeyDown={(e) => {
                    if (mentions.isVisible) {
                      if (e.key === 'ArrowDown') { e.preventDefault(); mentions.moveDown(); return; }
                      if (e.key === 'ArrowUp') { e.preventDefault(); mentions.moveUp(); return; }
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        const selected = mentions.filteredParticipants[mentions.selectedIndex];
                        if (selected) { setMessage(mentions.insertMention(selected, message)); }
                        return;
                      }
                      if (e.key === 'Escape') { e.preventDefault(); mentions.closeSuggestions(); return; }
                    }
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
              InputProps={{
                ...(selectedAttachment && {
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Chip
                        icon={selectedAttachment.type === 'file' && selectedAttachment.preview ? <ImageIcon /> : <FileIcon />}
                        avatar={
                          selectedAttachment.preview ? (
                            <Avatar
                              alt={selectedAttachment.label}
                              src={selectedAttachment.preview}
                            />
                          ) : undefined
                        }
                        label={selectedAttachment.label}
                        sx={{ maxWidth: '14rem' }}
                        onDelete={() => {
                          setSelectedAttachment(null);
                          setSelectedAsset(null);
                        }}
                      />
                    </InputAdornment>
                  )
                })
              }}
            />
            </Box>

            {/* Send Button */}
            <IconButton
  disabled={
    sendMessageLoading || 
    messagesLoading || 
    !conversationId ||
    (bubble && !bubble.can_create_posts) // ✅ NEW: Disable if posting not allowed
  }
  onClick={handleSendMessage}
  color="success"
  sx={{
    '&:disabled': {
      opacity: 0.5
    }
  }}
>
  {sendMessageLoading ? (
    <CircularProgress size={24} />
  ) : (
    <SendIcon />
  )}
</IconButton>
          </Box>
        </Box>

        {/* Message Cap Alert */}
        <Snackbar
          open={showMessageCapAlert}
          autoHideDuration={SNACKBAR_DURATION}
          onClose={() => setShowMessageCapAlert(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setShowMessageCapAlert(false)}
            severity="warning"
            sx={{ width: "100%" }}
          >
            Messages since last response: You are capped at{" "}
            {MAX_MESSAGES_WITHOUT_RESPONSE}
          </Alert>
        </Snackbar>

        {/* Reaction Snackbar */}
        <Snackbar
          open={snack.msg !== ''}
          autoHideDuration={3000}
          onClose={() => setSnack({ msg: '', sev: 'info' })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnack({ msg: '', sev: 'info' })}
            severity={snack.sev}
            sx={{ width: "100%" }}
          >
            {snack.msg}
          </Alert>
        </Snackbar>
      </Box>

      {/* Participants Panel Dialog */}
      <Dialog
        open={participantsOpen}
        onClose={handleToggleParticipants}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        sx={{ zIndex: getDialogZIndex() }}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 3 },
            height: { xs: '85vh', sm: '80vh' },
            maxHeight: { xs: '85vh', sm: '80vh' },
          }
        }}
      >
        <ParticipantsPanel
          conversationId={conversationId}
          onClose={handleToggleParticipants}
        />
      </Dialog>

      {/* Conversation Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={handleToggleDetails}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        sx={{ zIndex: getDialogZIndex() }}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 3 },
            height: { xs: '85vh', sm: '80vh' },
            maxHeight: { xs: '85vh', sm: '80vh' },
          }
        }}
      >
        <ConversationDetailsPanel
          conversationId={conversationId}
          onClose={handleToggleDetails}
        />
      </Dialog>

      {/* File Selection Dialog */}
      <SelectAssetDialog
        open={uploadDialogOpen}
        onClose={handleAssetDialogClose}
        selectedAsset={selectedAsset}
        onAssetClick={handleAssetClick}
      />

      {/* Casting Call Creation Dialog - CHAT MODE */}
      <CreateCallDialog
        open={castingCallDialogOpen}
        handleClose={() => setCastingCallDialogOpen(false)}
        onSuccess={handleCastingCallSuccess}
        mode="chat"
        conversationId={conversationId}
        bubbleId={bubbleId}
        bubbleName={bubble?.name}
        bubbleThemeColors={bubble?.theme_colors}
        defaultScopeType={bubbleId ? 'bubble' : 'global'}
        autoSelectBubble={!!bubbleId}
      />

      {/* Read Receipt Dialog */}
      <ReadReceipt
        open={currentMessage !== null}
        handleClose={() => setCurrentMessage(null)}
        message={currentMessage}
        participants={conversation?.participants}
        currentUsername={userId}
      />

      <AlertDialog alertState={dialogState} />
    </>
  );
});

export default BubbleChatScreen;